/** @prettier */
// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

import type { IRenderMode } from '#common/flow-types/build';
import type {
  NewsArticle,
  NewsArticleLocalizedContents,
} from '#common/flow-types/news';
import type { Subscription } from '#common/model/subscription';
import type { FiveSymbolLocale } from '#common/utils/locale';
import type { RootState } from '../../../reducers/modules/site-builder';
import type {
  NewsBlock,
  NewsBlockTemplate,
  LocalizedNewsArticle,
} from '../../../utils/types';

import {
  withHorizontalLayout,
  withMixedLayout,
  withSmallLayout,
  withSliderLayout,
} from '../../../layouts/news';
import {
  changeBlockById,
  checkoutBlock,
  saveBlockProcess,
} from '../../../reducers/modules/site-builder/blocks';
import { addScriptData } from '../../../reducers/modules/site-builder/scripts';
import Button from '../../../ui-components/Editor/Button';
import { EditorText } from '../../../ui-components/Editor/EditorText';
import { Media } from '../../../ui-components/Editor/Media';
import {
  readMoreNewsButtonText,
  showMoreButtonText,
} from '../../../utils/common-translations';
import {
  formatNewsDate,
  getNewsByLocale,
  filterSubscriptionsNamesForNews,
  DEFAULT_IMAGE,
  MIXED_NEWS_LAYOUT,
  HORIZONTAL_NEWS_LAYOUT,
  SMALL_NEWS_LAYOUT,
  SLIDER_NEWS_LAYOUT,
  SMALL_NEWS_AMOUNT,
  getNewsPrivateDescription,
} from '../../../utils/news-helper';
import {
  getNewsBlurredJoinBtnText,
  getNewsBlurredDescriptionText,
} from '../../../utils/translation';
import { getVideoThumbnail } from '../../../utils/video';
import BlurBlockWrapper from '../../Editor/BlurBlock/BlurBlockWrapper/BlurBlockWrapper';
import NewsBanner from './NewsBanner';
import EmptyNewsBanner from './NewsBanner/EmptyNewsBanner';
import NewsCard from './NewsCard';

import { LandingThemeType, PageThemeType } from '#common/types/model/landing';

import './news-modal.pcss';
import './style.pcss';

type Props = {
  locale: FiveSymbolLocale,
  block: NewsBlock,
  launcherId: string,
  news: Array<NewsArticle & NewsArticleLocalizedContents>,
  newsCount: number,
  checkoutBlock: (blockId: string) => void,
  renderMode: IRenderMode,
  isServerSideRender: boolean,
  fromList: boolean,
  saveBlockProcess: (block: NewsBlock) => Promise<NewsBlock>,
  changeBlockById: ({
    blockId: string,
    propName: string,
    propValue: any,
  }) => void,
  landingTheme: LandingThemeType,
  pageTheme: PageThemeType,
  addScriptData: (name: string, payload: Object) => void,
};

type NewsItemProps = {
  news: LocalizedNewsArticle,
  handleClick: (news: LocalizedNewsArticle) => void,
  handleScrollToPacks: () => void,
  locale: FiveSymbolLocale,
  landingTheme: LandingThemeType,
  pageTheme: PageThemeType,
  template: NewsBlockTemplate,
  subscriptionPlans: Subscription[],
};

type NewsTemplateType = {
  news: LocalizedNewsArticle[],
  handleClick: (news: LocalizedNewsArticle) => void,
  handleScrollToPacks: () => void,
  template: NewsBlockTemplate,
  launcherId: string,
  locale: FiveSymbolLocale,
  pageTheme: LandingThemeType,
  landingTheme: PageThemeType,
  subscriptionPlans?: Subscription[],
};

export class News extends PureComponent<Props> {
  static getImageUrl = (
    news: LocalizedNewsArticle,
    pageTheme: PageThemeType
  ): string => {
    if (news.image === DEFAULT_IMAGE) {
      const { pictureBackground, videoBackground } = pageTheme;
      return videoBackground.enable
        ? getVideoThumbnail(videoBackground.video)
        : pictureBackground.img;
    }
    return news.image;
  };

  static renderNewsItem = ({
    news,
    handleClick,
    handleScrollToPacks,
    locale,
    landingTheme,
    pageTheme,
    template,
    subscriptionPlans = [],
  }: NewsItemProps) => {
    const newsItemProps = {
      key: news.id,
      onClick: () => handleClick(news),
      onScrollToPacks: handleScrollToPacks,
      title: news.title,
      description: news.text_short,
      imageUrl: this.getImageUrl(news, pageTheme),
      date: formatNewsDate(news.date).toLocaleDateString(locale),
      category: '',
      buttonText: '',
      landingTheme,
      privateJoinText: '',
      privateDescription: '',
      isPrivate: false,
    };

    if (news.secure) {
      const newsItemPlans = filterSubscriptionsNamesForNews(
        subscriptionPlans,
        news.planIds,
        locale
      );
      newsItemProps.privateJoinText = getNewsBlurredJoinBtnText(locale);
      newsItemProps.privateDescription = getNewsPrivateDescription(
        getNewsBlurredDescriptionText(locale),
        newsItemPlans
      );
      newsItemProps.isPrivate = !!news.secure;
    }

    switch (template) {
      case MIXED_NEWS_LAYOUT:
      case HORIZONTAL_NEWS_LAYOUT:
      case SMALL_NEWS_LAYOUT:
        newsItemProps.category =
          news.category === 'default' ? '' : news.category;
        return <NewsCard {...newsItemProps} />;
      case SLIDER_NEWS_LAYOUT:
        newsItemProps.buttonText = readMoreNewsButtonText[locale];
        return <NewsBanner {...newsItemProps} />;
      default:
        return null;
    }
  };

  static renderNewsTemplate = (newsTemplateProps: NewsTemplateType) => {
    const {
      news,
      handleClick,
      handleScrollToPacks,
      template,
      launcherId,
      locale,
      pageTheme,
      landingTheme,
      subscriptionPlans = [],
    } = newsTemplateProps;

    if (launcherId === '' || news.length === 0) {
      return <EmptyNewsBanner value={launcherId} />;
    }

    const newsProps = {
      handleClick,
      handleScrollToPacks,
      locale,
      landingTheme,
      pageTheme,
      template,
      subscriptionPlans,
    };

    const renderedNewsItems = news.map((news) =>
      News.renderNewsItem({ news, ...newsProps })
    );

    switch (template) {
      case MIXED_NEWS_LAYOUT:
        return withMixedLayout(renderedNewsItems);
      case HORIZONTAL_NEWS_LAYOUT:
        return withHorizontalLayout(renderedNewsItems);
      case SMALL_NEWS_LAYOUT:
        return withSmallLayout(renderedNewsItems);
      case SLIDER_NEWS_LAYOUT:
        return withSliderLayout(renderedNewsItems, {
          color: landingTheme.textColor,
        });
      default:
        return null;
    }
  };

  onCheckoutBlock = () => {
    const { checkoutBlock, block } = this.props;
    checkoutBlock(block._id);
  };

  changeBlockValues = async () => {
    const { changeBlockById, saveBlockProcess, block } = this.props;
    changeBlockById({
      blockId: block._id,
      propName: 'values',
      propValue: { ...block.values },
    });
    await saveBlockProcess(block);
  };

  render() {
    const {
      addScriptData,
      block,
      renderMode,
      isServerSideRender,
      launcherId,
      locale,
      fromList,
      news,
      pageTheme,
      landingTheme,
      newsCount,
    } = this.props;
    const { values } = block;
    const { title, description, background, template } = values;

    if (isServerSideRender && launcherId) {
      addScriptData('launcherNewsInfo', {
        blockId: block._id,
        launcherId,
        template,
      });
    }

    const isBlur =
      isServerSideRender &&
      values.private &&
      values.private.length &&
      !fromList;

    const currentNews = [...news];

    if (!isServerSideRender && template === HORIZONTAL_NEWS_LAYOUT) {
      currentNews.length = SMALL_NEWS_AMOUNT;
    }

    return (
      <BlurBlockWrapper show={isBlur} currentBlock={block}>
        <Media
          className='custom-background'
          asset={background}
          onMediaButtonClick={this.onCheckoutBlock}
          renderMode={renderMode}
          isMainImage={false}
          showCustomBackground={background.enable}
          mediaType='background'
        />
        <div className='news block-content'>
          <div className='news__title-container'>
            {title.enable && (
              <EditorText
                size='h2'
                text={title[locale]}
                locale={locale}
                values={values}
                onBlur={this.changeBlockValues}
                field='title'
                isServerSideRender={isServerSideRender}
                onCheckoutBlock={this.onCheckoutBlock}
              />
            )}
            {description.enable && (
              <EditorText
                size='medium'
                text={description[locale]}
                locale={locale}
                values={values}
                onBlur={this.changeBlockValues}
                field='description'
                isServerSideRender={isServerSideRender}
                onCheckoutBlock={this.onCheckoutBlock}
              />
            )}
          </div>
          <div data-news-content>
            {!isServerSideRender &&
              News.renderNewsTemplate({
                news: currentNews,
                handleClick: () => {},
                handleScrollToPacks: () => {},
                template,
                launcherId,
                locale,
                pageTheme,
                landingTheme,
              })}
          </div>
          {template !== SLIDER_NEWS_LAYOUT && (
            <div className='load-more-wrapper'>
              <Button
                className='load-more-wrapper__button'
                hidden={!(newsCount > currentNews.length)}
                outline
                dataAttributes={{
                  'data-news-load-more-button': true,
                }}
              >
                {showMoreButtonText[locale]}
              </Button>
            </div>
          )}
        </div>
      </BlurBlockWrapper>
    );
  }
}

const connector: Function = connect(
  ({ blocks, landing, pages, launcherData }: RootState, ownProps) => ({
    block: blocks.blocks.find((block) => block._id === ownProps.id),
    locale: landing.locale,
    landingTheme: landing.landing.theme,
    pageTheme: pages.pages[pages.currentPage].theme,
    renderMode: landing.renderMode,
    ...getNewsByLocale({
      block: blocks.blocks.find((block) => block._id === ownProps.id),
      launcherData,
      locale: landing.locale,
    }),
  }),
  {
    checkoutBlock,
    changeBlockById,
    saveBlockProcess,
    addScriptData,
  }
);

export const NewsContainer = connector(News);
